import React, { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { motion } from "framer-motion";
import {
  TrendingUp,
  TrendingDown,
  PiggyBank,
  Wallet,
  ArrowLeft,
  ArrowUpRight,
  ArrowDownRight,
} from "lucide-react";
import api from "@/lib/api";
import Header from "@/components/Header";
import HamburgerMenu from "@/components/HamburgerMenu";
import { usePeriod } from "@/contexts/PeriodContext";
import { fmtShekel, MONTHS_HE } from "@/lib/utils";

export default function Dashboard() {
  const { month, year, view, setMonth, setYear, setView, refreshKey } = usePeriod();
  const [summary, setSummary] = useState(null);
  const [savingsTotal, setSavingsTotal] = useState(0);

  useEffect(() => {
    const load = async () => {
      const [sum, sav] = await Promise.all([
        api.get("/dashboard/summary", { params: { month, year, view } }),
        api.get("/savings"),
      ]);
      setSummary(sum.data);
      setSavingsTotal(
        (sav.data || []).reduce((s, x) => s + Number(x.amount || 0), 0)
      );
    };
    load();
  }, [month, year, view, refreshKey]);

  const incomeAfterTax = summary?.total_income_after_tax ?? summary?.total_income ?? 0;
  const expenseAfterCredit = summary?.total_expense_after_credit ?? summary?.total_expense ?? 0;
  const income = incomeAfterTax; // shown in cards
  const expense = expenseAfterCredit; // shown in cards
  const rawIncome = summary?.total_income || 0;
  const rawExpense = summary?.total_expense || 0;
  const incomeTaxOwed = summary?.income_tax_owed || 0;
  const expenseTaxCredit = summary?.expense_tax_credit || 0;
  const net = summary?.net_after_tax || 0;
  const cumulativeNet = summary?.cumulative_net || 0;
  const carriedOver = summary?.carried_over || 0;
  const daysInMonth = summary?.days_in_month || 30;
  const positive = cumulativeNet >= 0;
  const label = view === "month" ? `${MONTHS_HE[month - 1]} ${year}` : `שנת ${year}`;

  // Budget-per-remaining-days: computed here so tile can show budget accumulation
  const today = new Date();
  const isViewedMonth = today.getMonth() + 1 === month && today.getFullYear() === year;
  const todayDay = isViewedMonth ? today.getDate() : 1;
  const remainingDays = view === "month" ? Math.max(1, daysInMonth - todayDay + 1) : 365;
  // Divide the remaining net by the days LEFT, so the daily budget reflects
  // what the user can actually spend per remaining day (not per full month).
  const dailyAllowance = cumulativeNet > 0 ? cumulativeNet / remainingDays : 0;
  const [budgetExpanded, setBudgetExpanded] = useState(false);

  return (
    <div className="min-h-screen">
      <Header
        month={month}
        year={year}
        view={view}
        setMonth={setMonth}
        setYear={setYear}
        setView={setView}
        leftSlot={<HamburgerMenu />}
      />
      <main className="max-w-6xl mx-auto px-4 md:px-8 py-8 space-y-6">
        {/* Hero net card */}
        <motion.div
          initial={{ opacity: 0, y: 15 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.4 }}
          className="card-surface p-8 md:p-10 grain"
        >
          <div className="flex items-start justify-between flex-wrap gap-4 mb-6">
            <div>
              <div className="text-xs uppercase tracking-wider text-slate-500 font-heebo">
                מצב חשבון · {label}
              </div>
              <div className="flex items-baseline gap-3 mt-2 flex-wrap">
                <div
                  className="num-pop text-5xl md:text-6xl"
                  style={{ color: positive ? "var(--income)" : "var(--expense)" }}
                  data-testid="summary-net"
                >
                  {fmtShekel(cumulativeNet)}
                </div>
                <span
                  className={`chip ${positive ? "" : "chip-danger"}`}
                  style={
                    positive ? { background: "#DCFCE7", color: "#166534" } : {}
                  }
                >
                  {positive ? (
                    <ArrowUpRight size={14} />
                  ) : (
                    <ArrowDownRight size={14} />
                  )}
                  {positive ? "יתרה חיובית" : "יתרה שלילית"}
                </span>
              </div>
              <div className="text-xs text-slate-500 font-heebo mt-2" data-testid="cumulative-breakdown">
                יתרה נגררת מחודשים קודמים:{" "}
                <span
                  className="num-pop"
                  style={{ color: carriedOver >= 0 ? "#166534" : "#991B1B" }}
                >
                  {fmtShekel(carriedOver)}
                </span>{" "}
                · השינוי ב{label}:{" "}
                <span
                  className="num-pop"
                  style={{ color: net >= 0 ? "#166534" : "#991B1B" }}
                >
                  {net >= 0 ? "+" : ""}
                  {fmtShekel(net)}
                </span>
              </div>
            </div>
            <div className="flex items-start gap-3">
              <div
                className="w-16 h-16 rounded-2xl flex items-center justify-center"
                style={{ background: "#EFF5F1", color: "var(--brand)" }}
              >
                <Wallet size={30} />
              </div>
            </div>
          </div>
          <div className="h-2 rounded-full bg-slate-100 overflow-hidden flex" dir="ltr">
            {(() => {
              const total = income + expense || 1;
              const iw = Math.max(3, (income / total) * 100);
              const ew = Math.max(3, (expense / total) * 100);
              return (
                <>
                  <div style={{ width: `${iw}%`, background: "var(--income)" }} />
                  <div style={{ width: `${ew}%`, background: "var(--expense)" }} />
                </>
              );
            })()}
          </div>

          {/* Budget-remaining-days panel — moved from expenses to dashboard */}
          {view === "month" && (
            <div
              className="mt-6 rounded-xl p-4 border-2"
              style={
                cumulativeNet > 0
                  ? {
                      background: "linear-gradient(135deg, #ECFDF5 0%, #D1FAE5 100%)",
                      borderColor: "#6EE7B7",
                    }
                  : { background: "#FEE2E2", borderColor: "#FCA5A5" }
              }
              data-testid="budget-remaining-days"
            >
              {cumulativeNet <= 0 ? (
                <>
                  <div className="font-heebo font-bold text-red-800">ביזבוזים ליתרה</div>
                  <div className="text-sm text-red-700 mt-1">
                    אין יתרה חיובית לחלוקה. יש להגדיל הכנסות או להקטין הוצאות.
                  </div>
                </>
              ) : (
                <>
                  <div className="flex items-center justify-between flex-wrap gap-3">
                    <div>
                      <div className="font-heebo font-bold text-emerald-900">
                        ביזבוזים ליתרה
                      </div>
                      <div className="text-[11px] text-emerald-800 font-heebo mt-0.5">
                        נותרו {remainingDays} ימים · {fmtShekel(dailyAllowance)} ליום ממוצע
                      </div>
                    </div>
                    <div className="text-end">
                      <div className="text-[11px] text-emerald-800 font-heebo">יתרה זמינה לתקופה</div>
                      <div className="num-pop text-2xl text-emerald-900" data-testid="budget-available-total">
                        {fmtShekel(dailyAllowance * remainingDays)}
                      </div>
                    </div>
                  </div>
                  <button
                    type="button"
                    onClick={() => setBudgetExpanded((v) => !v)}
                    className="mt-3 text-xs font-heebo text-emerald-800 hover:text-emerald-900 flex items-center gap-1"
                    data-testid="budget-remaining-days-toggle"
                  >
                    {budgetExpanded ? "◤" : "◢"} תראה יום־יום שנותר בחודש ({remainingDays} ימים)
                  </button>
                  {budgetExpanded && (
                    <div className="mt-3 rounded-lg bg-white/70 divide-y divide-emerald-100 max-h-80 overflow-auto">
                      {Array.from({ length: remainingDays }).map((_, idx) => {
                        const dayNum = todayDay + idx;
                        const accumulated = dailyAllowance * (idx + 1);
                        return (
                          <div
                            key={dayNum}
                            className="flex items-center justify-between p-2 text-xs font-heebo"
                            data-testid={`budget-day-${dayNum}`}
                          >
                            <span className="text-emerald-900">
                              יום {dayNum}/{daysInMonth}
                              {idx === 0 && isViewedMonth && (
                                <span className="chip text-[10px] mr-2" style={{ background: "#059669", color: "#fff" }}>
                                  היום
                                </span>
                              )}
                            </span>
                            <span className="text-emerald-700">
                              אם לא בזבזת עד עכשיו — {fmtShekel(accumulated)}
                            </span>
                          </div>
                        );
                      })}
                    </div>
                  )}
                </>
              )}
            </div>
          )}
        </motion.div>

        {/* Nav cards */}
        <div className="grid grid-cols-1 md:grid-cols-3 gap-5">
          <NavCard
            to="/app/incomes"
            label="הכנסות"
            amount={income}
            hint={incomeTaxOwed > 0 ? `אחרי תשלום מסים (מגולם: ${fmtShekel(rawIncome)})` : null}
            color="var(--income)"
            bg="#DCFCE7"
            icon={<TrendingUp size={22} />}
            testid="nav-incomes"
            delay={0.05}
          />
          <NavCard
            to="/app/expenses"
            label="הוצאות"
            amount={expense}
            hint={expenseTaxCredit > 0 ? `אחרי זיכוי מסים (מגולם: ${fmtShekel(rawExpense)})` : null}
            color="var(--expense)"
            bg="#FDECEC"
            icon={<TrendingDown size={22} />}
            testid="nav-expenses"
            delay={0.1}
          />
          <NavCard
            to="/app/savings"
            label="חסכונות"
            amount={savingsTotal}
            color="var(--savings)"
            bg="#DBEAFE"
            icon={<PiggyBank size={22} />}
            testid="nav-savings"
            delay={0.15}
            subtitle="סה״כ צבירה"
          />
        </div>

        <footer className="text-center text-xs text-slate-400 py-8">
          © {new Date().getFullYear()} · דניאל חסין · אזור פיננסי אישי
        </footer>
      </main>
    </div>
  );
}

function BreakdownTile({ label, value, subtitle, bg, text, testid }) {
  const positive = value >= 0;
  return (
    <div
      className="rounded-2xl p-4 border-2"
      style={{ background: bg, borderColor: text + "33" }}
      data-testid={testid}
    >
      <div className="text-xs font-heebo font-semibold" style={{ color: text }}>
        {label}
      </div>
      <div
        className="num-pop text-2xl mt-1"
        style={{ color: positive ? text : "#B91C1C" }}
      >
        {fmtShekel(value)}
      </div>
      {subtitle && (
        <div className="text-[11px] mt-1 font-heebo" style={{ color: text, opacity: 0.75 }}>
          {subtitle}
        </div>
      )}
    </div>
  );
}

function NavCard({ to, label, amount, color, bg, icon, testid, delay = 0, subtitle, hint }) {
  return (
    <motion.div
      initial={{ opacity: 0, y: 12 }}
      animate={{ opacity: 1, y: 0 }}
      transition={{ duration: 0.3, delay }}
    >
      <Link to={to} className="nav-card block" data-testid={testid}>
        <div className="flex items-start justify-between mb-5">
          <div
            className="w-12 h-12 rounded-2xl flex items-center justify-center"
            style={{ background: bg, color }}
          >
            {icon}
          </div>
          <ArrowLeft size={18} className="text-slate-400" />
        </div>
        <div className="text-xs uppercase text-slate-500 font-heebo tracking-wider">
          {subtitle || label}
        </div>
        <div className="mt-1 flex items-baseline justify-between">
          <div className="num-pop text-3xl" style={{ color }} data-testid={`${testid}-amount`}>
            {fmtShekel(amount)}
          </div>
          <div className="font-heebo text-slate-700 font-semibold">{label}</div>
        </div>
        {hint && (
          <div className="mt-1 text-[10px] text-slate-500 font-heebo" data-testid={`${testid}-hint`}>
            {hint}
          </div>
        )}
      </Link>
    </motion.div>
  );
}
